import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useState,
  useRef,
  Fragment,
} from 'react';
import _ from 'lodash';

import { cn } from '@/libs';
import { AlertMessage, Button } from '@/ui-kit';

import type {
  DynamicFormData,
  FormDomain,
  FormOnSubmitFunction,
  FormPrimitiveValues,
} from '../typings';
import { FormContext } from '../contexts';
import {
  FORM_VALIDATION_SUMMARY_FOOTER,
  FORM_VALIDATION_SUMMARY_TITLE,
  getFormValidationSummaryDescription,
} from '../constants/formValidationMessages';
import {
  useFieldsGroupItemCount,
  type ClientOwnedFields,
} from '../hooks';
import { FormProvider, type FormRenderContext } from '../core';
import {
  getOrderedValidationErrors,
  jumpToField,
  overlayClientOwnedValues,
  type OrderedValidationError,
  type TrailingValidationError,
} from '../utils';
import { collectClearableFieldKeys } from '../utils/clearableFieldKeys';

import './DynamicForm.scss';

/**
 * Jumps to the first invalid field (top-to-bottom) after a failed submit, and
 * focuses it — unless it resolves to a container (array/group) element opting
 * out via `data-jump-scroll-only`, which only scrolls (see `jumpToField`).
 */
function JumpToFirstErrorOnSubmit({
  orderedErrors,
  submitCount,
  isValidating,
  enabled,
}: {
  orderedErrors: OrderedValidationError[];
  submitCount: number;
  isValidating: boolean;
  enabled: boolean;
}) {
  const jumpedForSubmitCount = useRef(0);

  useLayoutEffect(() => {
    if (!enabled || submitCount === 0 || isValidating) {
      return;
    }

    if (jumpedForSubmitCount.current >= submitCount) {
      return;
    }

    jumpedForSubmitCount.current = submitCount;

    if (orderedErrors.length > 0) {
      jumpToField(orderedErrors[0].name);
    }
  }, [enabled, submitCount, isValidating, orderedErrors]);

  return null;
}

/** `raw` with the client-owned screen values kept over it, or `raw` itself if ownership is unknown. */
function keepClientOwnedValues(
  raw: DynamicFormData['initialValues'] | undefined,
  liveValues: DynamicFormData['initialValues'] | undefined,
  previousRaw: DynamicFormData['initialValues'] | undefined,
  clientOwnedFields: ClientOwnedFields | undefined,
) {
  if (!raw || !clientOwnedFields) return raw;

  try {
    if (!liveValues) {
      // A fresh mount has no screen, and cannot tell whether this snapshot answers a save of
      // a held value (the unmount flush resends a failed one), so it keeps every one.
      const heldValues = clientOwnedFields.getHeldValues();
      return overlayClientOwnedValues(
        raw,
        { ...raw, ...heldValues },
        new Set(Object.keys(heldValues)),
      );
    }

    return overlayClientOwnedValues(
      raw,
      liveValues,
      clientOwnedFields.getOwnedFieldIds(liveValues, previousRaw),
    );
  } catch (error) {
    // Resetting every field to the server's, as before, beats leaving Formik un-reset.
    console.error('Client-owned field ownership check failed', error);
    return raw;
  }
}

/** Focuses the first candidate still on the page that takes focus, if focus fell to the body. */
function returnFocusFromBody(candidates: Array<Element | null>) {
  const focused = document.activeElement;
  if (focused && focused !== document.body) return;

  candidates.some(candidate => {
    if (
      !(candidate instanceof HTMLElement) ||
      candidate === document.body ||
      !candidate.isConnected ||
      candidate.closest('[inert]')
    ) {
      return false;
    }
    candidate.focus();
    return document.activeElement === candidate;
  });
}

type FormikFormProps<T> = {
  initialValues?: T;
  onSubmit: FormOnSubmitFunction<T>;
  children: React.ReactNode;
  submitLabel?: string;
  submitDisabled?: boolean;
  className?: string;
  extraButtons?: Array<React.ReactNode>;
  submitButtonFirst?: boolean;
  hideSubmitButton?: boolean;
  /** When true (default), suppresses the validation summary above Continue. */
  hideValidationSummary?: boolean;
  /** Summary rows for errors raised by `children` rendered below the field tree. */
  trailingErrorRows?: TrailingValidationError[];
  domain: FormDomain;
  /** Called when focus leaves any field — the natural "this field is done" boundary. */
  onFieldBlur?: () => void;
  /** Fields whose screen value a server snapshot must not overwrite; without it a snapshot resets every field. */
  clientOwnedFields?: ClientOwnedFields;
};

type FormProps = FormikFormProps<DynamicFormData['initialValues']> &
  Partial<Exclude<DynamicFormData, 'initialValues'>>;

export type DynamicFormHandle = {
  reinitialize: () => void;
  /** Puts the stored value back in the named fields, leaving every other edit on screen. */
  resetFields: (fieldIds: string[]) => void;
  submitForm: () => void;
  hasErrors: () => boolean;
  validateForm: () => Promise<void>;
};

export const DynamicForm = forwardRef<DynamicFormHandle, FormProps>(
  (
    {
      initialValues,
      onSubmit,
      validationSchema,
      formFieldGroups,
      children,
      submitLabel = 'Continue',
      className,
      extraButtons,
      submitButtonFirst = true,
      hideSubmitButton = false,
      submitDisabled = false,
      hideValidationSummary = true,
      trailingErrorRows,
      domain,
      onFieldBlur,
      clientOwnedFields,
    },
    ref,
  ) => {
    const actionsRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const inertTasksRef = useRef(0);
    // Where an inert task hands focus back: by the time it starts, a confirm dialog outside the
    // form may hold focus instead of the control that opened it.
    const lastFocusedInFormRef = useRef<Element | null>(null);
    const submitFormRef = useRef<() => Promise<void>>();

    const setFieldValueRef =
      useRef<
        FormRenderContext<DynamicFormData['initialValues']>['setFieldValue']
      >();

    const formErrorsRef = useRef<Record<string, string | undefined>>({});

    const validateFormRef = useRef<
      (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        values?: any,
      ) => Promise<Record<string, string | undefined>> | undefined
    >();

    // Seeded with the held values already laid over, so Formik and the change tracker start
    // from what the provider last saw rather than resetting to it a render later.
    const [formInitialValues, setFormInitialValues] = useState<
      DynamicFormData['initialValues'] | undefined
    >(() =>
      keepClientOwnedValues(
        initialValues,
        undefined,
        undefined,
        clientOwnedFields,
      ),
    );

    const maxItems = useFieldsGroupItemCount(formFieldGroups ?? []);

    // Formik's resets to a new snapshot, counted as each happens and read during Formik's render
    // like Formik's own values, so the render that first shows a reset is the one carrying its
    // count. A count set from the apply would land a commit early, on the old screen plus
    // whatever the fields wrote as the snapshot reshaped them.
    const resetCountRef = useRef(0);
    const countReset = useCallback(() => {
      resetCountRef.current += 1;
    }, []);

    // The snapshot as the server sent it, before any screen value was kept over it —
    // what Discard puts back.
    const serverValuesRef = useRef(initialValues);
    // Formik's values as of its latest render, so a snapshot is weighed against what is on
    // screen when it lands rather than what a callback closed over earlier.
    const liveValuesRef = useRef<DynamicFormData['initialValues'] | undefined>();
    const clientOwnedFieldsRef = useRef(clientOwnedFields);
    clientOwnedFieldsRef.current = clientOwnedFields;

    // The per-field analogue of `reinitializeCount`: the ids `resetFields` last wrote,
    // and a counter that bumps on every call. One state object rather than two, so the
    // ids and the count can never be read from different renders.
    const [fieldReset, setFieldReset] = useState<{
      count: number;
      fieldIds: string[];
    }>({ count: 0, fieldIds: [] });

    // Single entry point for changing `formInitialValues`, shared by the `initialValues`-prop
    // effect below and the imperative `reinitialize()` handle.
    const applyInitialValues = useCallback(
      (raw: DynamicFormData['initialValues'] | undefined) => {
        const next = keepClientOwnedValues(
          raw,
          liveValuesRef.current,
          serverValuesRef.current,
          clientOwnedFieldsRef.current,
        );
        serverValuesRef.current = raw;
        setFormInitialValues(next);
      },
      [],
    );

    useImperativeHandle(ref, () => ({
      reinitialize: () => {
        applyInitialValues(initialValues);
      },
      // Per-field writes, deliberately bypassing `applyInitialValues`: an edit the
      // API refused never reached the server, so `initialValues` stays deep-equal,
      // the counter never bumps and the reinitialize path would reset nothing. Not
      // Formik's resetForm either — that reverts every field to the page-load
      // snapshot, re-saving stale values over ones the provider already stored.
      resetFields: (fieldIds: string[]) => {
        const setFieldValue = setFieldValueRef.current;
        // The raw snapshot, not `formInitialValues`: that may hold the very screen value
        // being discarded, kept over the server's.
        const serverValues = serverValuesRef.current;
        if (!setFieldValue || !serverValues) return;

        // Built only when some id was never seeded — a leaf added after the fetch has
        // no `initialValues` entry to go back to, so it goes back to empty instead.
        let clearedValueByKey: Map<string, FormPrimitiveValues> | undefined;

        // Only the ids a value was actually written for: the change tracker folds these
        // into its baseline, and an id nothing was written for has no baseline to move.
        const writtenFieldIds: string[] = [];

        for (const fieldId of fieldIds) {
          // Own-property only: `in` walks the prototype, so an id like `toString`
          // would otherwise write a value Formik never held.
          if (Object.prototype.hasOwnProperty.call(serverValues, fieldId)) {
            // Cloned so `values` never aliases the raw copy: an in-place edit of a shared
            // array would change what Discard and the staged rule read back from it.
            void setFieldValue(fieldId, _.cloneDeep(serverValues[fieldId]));
            writtenFieldIds.push(fieldId);
            continue;
          }

          if (!clearedValueByKey) {
            clearedValueByKey = new Map(
              (formFieldGroups ?? [])
                .flatMap(collectClearableFieldKeys)
                .map(({ key, clearedValue }): [string, FormPrimitiveValues] => [
                  key,
                  clearedValue,
                ]),
            );
          }

          const clearedValue = clearedValueByKey.get(fieldId);
          // No entry means no field of that id in this form; writing one would
          // register a change nobody made.
          if (clearedValue === undefined) continue;

          void setFieldValue(fieldId, clearedValue);
          writtenFieldIds.push(fieldId);
        }

        if (writtenFieldIds.length === 0) return;

        // Bumped in the same synchronous block as the setFieldValue calls above, so
        // React commits the marker and the values it describes together — see the
        // ordering comment in useFormChangeTracker for why that is load-bearing.
        setFieldReset(previous => ({
          count: previous.count + 1,
          fieldIds: writtenFieldIds,
        }));
      },
      submitForm: () => {
        submitFormRef.current?.();
      },
      hasErrors: () => {
        return (
          formErrorsRef.current && Object.keys(formErrorsRef.current).length > 0
        );
      },
      validateForm: async () => {
        await validateFormRef.current?.();
      },
    }));

    // Layout, not passive: a snapshot store update renders synchronously, and only a layout
    // effect resets Formik in that same task. With a passive one Formik resets a task later,
    // over any keystroke typed after ownership was weighed.
    useLayoutEffect(() => {
      // Already applied by the initial state; weighing it again with the screen it produced
      // would release a held value the ownership rules take as answered.
      if (initialValues === serverValuesRef.current) return;
      applyInitialValues(initialValues);
    }, [initialValues, applyInitialValues]);

    // Set on the element directly: React 18's types lack `inert`, and the form must lock in the
    // click's own task, before a render could. Counted, so an overlapping task keeps it locked.
    const whileInert = useCallback(
      async <T,>(task: () => T | Promise<T>): Promise<T> => {
        const form = formRef.current;
        const focusedAtStart = document.activeElement;
        inertTasksRef.current += 1;
        form?.setAttribute('inert', '');
        form?.setAttribute('aria-busy', 'true');
        try {
          return await task();
        } finally {
          inertTasksRef.current -= 1;
          if (inertTasksRef.current === 0) {
            form?.removeAttribute('inert');
            form?.removeAttribute('aria-busy');
            // The browser dropped focus on the body when the form went inert. A form the
            // refetch replaced is gone, and the new one leaves focus to the page.
            if (form?.isConnected) {
              returnFocusFromBody([focusedAtStart, lastFocusedInFormRef.current]);
            }
          }
        }
      },
      [],
    );

    return (
      formInitialValues &&
      validationSchema &&
      formFieldGroups && (
        <FormProvider
          headless
          enableReinitialize
          onReset={countReset}
          initialValues={formInitialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({
            values,
            dirty,
            errors,
            submitCount,
            isValidating,
            submitForm,
            validateForm,
            setFieldValue,
          }: FormRenderContext<DynamicFormData['initialValues']>) => {
            const showValidationSummary =
              !hideValidationSummary &&
              submitCount > 0 &&
              Object.keys(errors).length > 0;

            submitFormRef.current = submitForm;
            formErrorsRef.current = errors;
            validateFormRef.current = validateForm;
            setFieldValueRef.current = setFieldValue;
            liveValuesRef.current = values;

            const orderedErrors = getOrderedValidationErrors(
              formFieldGroups,
              errors,
              trailingErrorRows,
            );

            return (
              <form
                ref={formRef}
                className={cn('df', className)}
                onBlur={() => onFieldBlur?.()}
                onFocus={e => {
                  // React also bubbles focus from portals, such as a dialog the form opened.
                  if (e.currentTarget.contains(e.target)) {
                    lastFocusedInFormRef.current = e.target;
                  }
                }}
                onSubmit={e => {
                  e.preventDefault();

                  void validateForm().then(validationErrors => {
                    if (Object.keys(validationErrors).length > 0) {
                      console.table(
                        Object.fromEntries(
                          Object.keys(validationErrors).map(key => [
                            key,
                            {
                              error: validationErrors[key],
                              value: values[key],
                            },
                          ]),
                        ),
                      );
                    }

                    void submitForm();
                  });
                }}
                autoComplete="off"
              >
                <JumpToFirstErrorOnSubmit
                  orderedErrors={orderedErrors}
                  submitCount={submitCount}
                  isValidating={isValidating}
                  enabled={!hideValidationSummary}
                />
                <FormContext.Provider
                  value={{
                    values,
                    initialValues: formInitialValues,
                    dirty,
                    formFieldGroups,
                    maxItems,
                    domain,
                    reinitializeCount: resetCountRef.current,
                    fieldResetCount: fieldReset.count,
                    fieldResetIds: fieldReset.fieldIds,
                    whileInert,
                  }}
                >
                  {children}
                </FormContext.Provider>

                <div
                  ref={actionsRef}
                  className={cn('df__actions', {
                    'df__actions--submit-first': submitButtonFirst,
                    'df__actions--submit-last': !submitButtonFirst,
                  })}
                >
                  {showValidationSummary && (
                    <div className="df__validation-summary">
                      <AlertMessage
                        variant="danger"
                        title={FORM_VALIDATION_SUMMARY_TITLE}
                        description={getFormValidationSummaryDescription(
                          orderedErrors.length,
                        )}
                        icon="left"
                        className="alert-message-full-width"
                        style={{
                          justifyContent: 'center'
                        }}
                      />
                      {orderedErrors.length > 0 && (
                        <div className="df__validation-summary-body">
                          <ul className="df__validation-summary-list">
                            {orderedErrors.map(({ name, label }) => (
                              <li key={`${name}:${label}`}>
                                <a
                                  href={`#${name}`}
                                  onClick={e => {
                                    e.preventDefault();
                                    jumpToField(name);
                                  }}
                                >
                                  {label}
                                </a>
                              </li>
                            ))}
                          </ul>
                          <p className="df__validation-summary-footer">
                            {FORM_VALIDATION_SUMMARY_FOOTER}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {!hideSubmitButton && (
                    <Button type="submit" disabled={submitDisabled}>
                      {submitLabel}
                    </Button>
                  )}
                  {extraButtons?.map((button, index) =>
                    button !== null ? (
                      <Fragment key={index}>{button}</Fragment>
                    ) : null,
                  )}
                </div>
              </form>
            );
          }}
        </FormProvider>
      )
    );
  },
);
